import { c as _c } from "react/compiler-runtime";
import * as RadixSelect from '@radix-ui/react-select';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cx } from 'class-variance-authority';
import { forwardRef } from 'react';
import BaseSelectTrigger from '../../../internal/base-select-trigger';
import type { Size } from '../../select.types';
type SelectTriggerProps = {
  value: string;
  placeholder: string;
  disabled: boolean;
  hasError: boolean;
  size: Size;
  className?: string;
  ariaLabel?: string;
  customTrigger?: React.ReactNode;
  findOptionLabel: (value: string) => string | React.ReactNode;
  id?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
};
const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>((t0, ref) => {
  const $ = _c(60);
  let ariaLabel;
  let autoFocus;
  let className;
  let customTrigger;
  let disabled;
  let findOptionLabel;
  let hasError;
  let id;
  let onBlur;
  let onFocus;
  let placeholder;
  let rest;
  let size;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      placeholder,
      disabled,
      hasError,
      size,
      className,
      ariaLabel,
      customTrigger,
      findOptionLabel,
      id,
      onBlur,
      onFocus,
      autoFocus,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = autoFocus;
    $[3] = className;
    $[4] = customTrigger;
    $[5] = disabled;
    $[6] = findOptionLabel;
    $[7] = hasError;
    $[8] = id;
    $[9] = onBlur;
    $[10] = onFocus;
    $[11] = placeholder;
    $[12] = rest;
    $[13] = size;
    $[14] = value;
  } else {
    ariaLabel = $[1];
    autoFocus = $[2];
    className = $[3];
    customTrigger = $[4];
    disabled = $[5];
    findOptionLabel = $[6];
    hasError = $[7];
    id = $[8];
    onBlur = $[9];
    onFocus = $[10];
    placeholder = $[11];
    rest = $[12];
    size = $[13];
    value = $[14];
  }
  if (customTrigger) {
    let t1;
    if ($[15] !== className) {
      t1 = cx("flex flex-row focus-visible:outline-none cursor-pointer disabled:cursor-default", className);
      $[15] = className;
      $[16] = t1;
    } else {
      t1 = $[16];
    }
    let t2;
    if ($[17] !== value) {
      t2 = <VisuallyHidden><RadixSelect.Value>{value}</RadixSelect.Value></VisuallyHidden>;
      $[17] = value;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    let t3;
    if ($[19] !== customTrigger || $[20] !== rest || $[21] !== t1 || $[22] !== t2) {
      t3 = <div {...rest} translate="no" className={t1}>{customTrigger}{t2}</div>;
      $[19] = customTrigger;
      $[20] = rest;
      $[21] = t1;
      $[22] = t2;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    let t4;
    if ($[24] !== ariaLabel || $[25] !== autoFocus || $[26] !== disabled || $[27] !== id || $[28] !== onBlur || $[29] !== onFocus || $[30] !== ref || $[31] !== t3) {
      t4 = <RadixSelect.Trigger id={id} asChild={true} onBlur={onBlur} onFocus={onFocus} ref={ref} disabled={disabled} tabIndex={0} aria-label={ariaLabel} autoFocus={autoFocus}>{t3}</RadixSelect.Trigger>;
      $[24] = ariaLabel;
      $[25] = autoFocus;
      $[26] = disabled;
      $[27] = id;
      $[28] = onBlur;
      $[29] = onFocus;
      $[30] = ref;
      $[31] = t3;
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    return t4;
  }
  const t1 = !value;
  let t2;
  if ($[33] !== findOptionLabel || $[34] !== value) {
    t2 = value ? findOptionLabel(value) : null;
    $[33] = findOptionLabel;
    $[34] = value;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  let t3;
  if ($[36] !== placeholder || $[37] !== t2) {
    t3 = <RadixSelect.Value placeholder={placeholder} className="truncate text-body-2">{t2}</RadixSelect.Value>;
    $[36] = placeholder;
    $[37] = t2;
    $[38] = t3;
  } else {
    t3 = $[38];
  }
  let t4;
  if ($[39] !== autoFocus || $[40] !== disabled || $[41] !== hasError || $[42] !== id || $[43] !== onBlur || $[44] !== ref || $[45] !== size || $[46] !== t1 || $[47] !== t3) {
    t4 = <BaseSelectTrigger size={size} data-has-error={hasError} isEmpty={t1} tabIndex={0} id={id} ref={ref} onBlur={onBlur} disabled={disabled} className="flex focus-visible:outline-none cursor-pointer disabled:cursor-default" autoFocus={autoFocus}>{t3}</BaseSelectTrigger>;
    $[39] = autoFocus;
    $[40] = disabled;
    $[41] = hasError;
    $[42] = id;
    $[43] = onBlur;
    $[44] = ref;
    $[45] = size;
    $[46] = t1;
    $[47] = t3;
    $[48] = t4;
  } else {
    t4 = $[48];
  }
  let t5;
  if ($[49] !== ariaLabel || $[50] !== autoFocus || $[51] !== className || $[52] !== disabled || $[53] !== onFocus || $[54] !== t4) {
    t5 = <RadixSelect.Trigger aria-label={ariaLabel} asChild={true} className={className} autoFocus={autoFocus} disabled={disabled} onFocus={onFocus}>{t4}</RadixSelect.Trigger>;
    $[49] = ariaLabel;
    $[50] = autoFocus;
    $[51] = className;
    $[52] = disabled;
    $[53] = onFocus;
    $[54] = t4;
    $[55] = t5;
  } else {
    t5 = $[55];
  }
  let t6;
  if ($[56] !== className || $[57] !== rest || $[58] !== t5) {
    t6 = <div {...rest} translate="no" className={className}>{t5}</div>;
    $[56] = className;
    $[57] = rest;
    $[58] = t5;
    $[59] = t6;
  } else {
    t6 = $[59];
  }
  return t6;
});
export default SelectTrigger;